import camelcaseKeys from 'camelcase-keys';
import snakecase from 'snakecase-keys';
import { saveAs } from 'file-saver';
import { type UseMutateAsyncFunction } from '@tanstack/react-query';
import { t } from 'i18next';

import HttpError from '../../utils/apis/HttpError';
import store from '../../store/store';
import { buildHeaders, buildUrl, buildRequest, fetchRequest } from '../../utils/apis/RestApi';
import { type HttpMethod } from '../../utils/apis/restApiUtils';
import {
  type GetInterventionsIdsParams,
  type WorkshopIntervention,
  type InterventionState,
  type SparePartWorkshop,
  type CreationResponse,
  type RawShippingReturn,
  type ShippingResponse,
  type CreationResponseRaw,
  type StateParam,
  type InterventionProduct,
  type ToDoEvent,
  type PurchaseIntervention,
  type WorkshopInterventionRaw,
} from './intervention.type';
import { handleHttpErrors, handle404Errors } from '../../business/helpers';
import { truncateId } from '../../utils/utils';
import { printBlob } from '../../utils/utils-print';
import { createLog } from '../logger';
import { fillInterventionsInformation } from '../../business/interventionHelper';
import { type Intervention } from '../../components/InterventionsPlanningResult/InterventionPlanningResult.type';
import { InterventionStatus } from '../../business/interventionButtonMenu.const';
import { chunkArray } from '../ilink';
import { type SearchIntervention } from '../../components/TableSearch/TableSearch';
import { getCustomerName } from '../../business/customerHelper';
import { fetchFullCustomerById } from '../member/member';
import { getArticlesDetails } from '../masterdata/masterdata';
import { formatISOWithTimeZone, getCurrentDate, getEndOfDay, getStartOfDay } from '../../utils/date/date-utils';
import { type ParcelIntervention, type Parcel, type ParcelNumberRaw } from './parcel.type';
import { type UsePatchInterventionMutationParams } from './mutations.types';
import { WORKSHOP, WORKSHOP_PROXY } from '../constants';
import { getInterventionCustomerAlert } from './getInterventionCustomerAlert';
import { CustomerCommunicationValues } from '../startup/startup.const';
import { buildWorkshopV3Request } from './intervention.fetch';
import { type InterventionStatesPagination } from './interventions.types';
import { type NullableDate } from '../../types/globals';

const logger = createLog('api/interventions');

const buildBody = <T extends object>(body?: T | FormData, emptyString?: boolean, isFileRequest?: boolean) =>
  isFileRequest ? (body as FormData) : JSON.stringify(body, (_, value: string) => (!emptyString && value === '' ? undefined : value));

interface WksFetchProps<T extends object> {
  url: string;
  method: HttpMethod;
  body?: T | FormData;
  emptyString?: boolean;
  isFileRequest?: boolean;
}

const buildWorkshopToolsRequest = <T extends object>({ url, method, body, emptyString, isFileRequest }: WksFetchProps<T>): Promise<Response> => {
  const headers = buildHeaders(true);
  const urlbuilt = buildUrl('ICARE_BACK', url);
  const request = buildRequest(urlbuilt, method, headers, buildBody(body, emptyString, isFileRequest), isFileRequest);
  return fetchRequest(request);
};

export const getInterventionsIds = (getInterventionsIdsParams: GetInterventionsIdsParams, storeId: number): Promise<ToDoEvent[]> => {
  const { toDoEvents = [], processes, fromDate, toDate, nextParams = '' } = getInterventionsIdsParams;
  let params = '';

  if (processes) {
    params = `${params}&processes=${processes}`;
  }

  if (fromDate) {
    params = `${params}&start_date=${formatISOWithTimeZone(getStartOfDay(fromDate))}`;
  }

  if (toDate) {
    params = `${params}&end_date=${formatISOWithTimeZone(getEndOfDay(toDate))}`;
  }

  if (nextParams) {
    params = `${params}&${nextParams}`;
  }

  const url = `${WORKSHOP_PROXY}/v2/stores/${storeId}/states/in_progress/interventions?${params || ''}`;
  return buildWorkshopToolsRequest({ url, method: 'GET' })
    .then(response => (response.status === 204 ? [] : response.json()))
    .then((interventions: any[]) =>
      toDoEvents.concat(
        interventions.map(intervention => ({
          date: new Date(intervention.availability_date),
          interventionIds: intervention.document_numbers,
        }))
      )
    )
    .catch(httpError => handleHttpErrors(httpError, 'intervention', logger, { url }));
};

const buildWorkshopInterventionFromRaw = (interventionRaw: any): WorkshopIntervention => {
  const intervention = { ...interventionRaw, deposited_product: interventionRaw.deposited_product || {} };
  const creationDate = new Date(intervention.creation_date);
  const availabilityDate = intervention.availability_date ? new Date(intervention.availability_date) : null;
  const purchaseDate = intervention.deposited_product.purchase_date ? new Date(intervention.deposited_product.purchase_date) : undefined;
  const workshopIntervention = camelcaseKeys<WorkshopIntervention>(intervention, { deep: true });

  return {
    ...workshopIntervention,
    creationDate,
    availabilityDate,
    depositedProduct: {
      ...workshopIntervention.depositedProduct,
      purchaseDate,
    },
  };
};

export const getWorkshopInterventionById = (
  interventionId: string,
  isInvoice: boolean,
  includes: string[],
  storeId: number
): Promise<WorkshopIntervention> => {
  const url = `${WORKSHOP_PROXY}/v3/stores/${storeId}/interventions/${
    isInvoice ? 'invoice_numbers' : 'document_numbers'
  }/${interventionId}?include=${includes}`;
  return buildWorkshopV3Request(url, 'GET')
    .then(response => response.json())
    .then((result: WorkshopInterventionRaw) =>
      result.deposited_product?.item_code
        ? getArticlesDetails([result.deposited_product.item_code]).then(([model]) => ({
            ...result,
            deposited_product: {
              ...result.deposited_product,
              model_code: Number(model.modelId),
              label: model.modelLib,
            },
          }))
        : result
    )
    .then(buildWorkshopInterventionFromRaw)
    .catch(httpError => {
      if (httpError.response?.status === 404) {
        throw t('error.message.invalid.searched.text');
      }

      if (httpError.response?.status === 403) {
        return httpError.response.json().then((errorDetails: any) => {
          if (errorDetails.error_id === '23') {
            throw t('toaster.error.intervention.not.available.details', {
              documentNumber: truncateId(interventionId),
              stores: errorDetails.error.values.join(' - '),
            });
          } else {
            throw t('toaster.error.forbidden.access');
          }
        });
      }
      return handleHttpErrors(httpError, 'intervention', logger, { url, interventionId });
    });
};

export const getEmailAlert = (email: string | undefined, customerCommunication: string) => {
  if (customerCommunication === CustomerCommunicationValues.Both || customerCommunication === CustomerCommunicationValues.Email) {
    return Boolean(email);
  }
  return false;
};

export const getSMSAlert = (phoneNumber: string | undefined, customerCommunication: string) => {
  if (customerCommunication === CustomerCommunicationValues.Both || customerCommunication === CustomerCommunicationValues.Phone) {
    return Boolean(phoneNumber);
  }
  return false;
};

const getCreationResponse = (result: CreationResponseRaw): CreationResponse => ({
  docNumber: result.document_number,
  state: {
    stateId: result.state.state_id,
    stateLabel: result.state.state_label,
  },
});

export const addFilesToIntervention = (docNumber: string, files: FileList | never[], storeId: number) => {
  const url = `${WORKSHOP}/interventions/${storeId}/intervention/${docNumber}/file`;
  return Array.from(files).map(file => {
    const form = new FormData();
    form.append('file', file);
    return buildWorkshopToolsRequest({ url, method: 'POST', body: form, emptyString: false, isFileRequest: true }).catch(httpError =>
      handleHttpErrors(httpError, 'intervention', logger, { url, docNumber, fileName: file.name })
    );
  });
};

/**
 *
 * @deprecated do not use this undocumented method
 */
export const updateIntervention = async (
  docNumber: string,
  value: string | number | boolean | InterventionProduct,
  fieldName: string,
  patchIntervention: UseMutateAsyncFunction<unknown, Error, UsePatchInterventionMutationParams, unknown>
): Promise<void> => {
  const fields = fieldName.split('.');
  const updatedValue = fields.length === 1 ? { [fields[0]]: value } : { [fields[0]]: { [fields[1]]: value } };
  await patchIntervention({
    interventionId: docNumber,
    payload: updatedValue,
  });
};

export const updateCircuit = (docNumber: string, isLocal: boolean, realisationSite: number, storeId: number): Promise<InterventionState> => {
  const url = `${WORKSHOP_PROXY}/v2/stores/${storeId}/interventions/document_numbers/${docNumber}/circuit`;
  const model = snakecase({ isLocal, realisationSite });
  return buildWorkshopToolsRequest({ url, method: 'PATCH', body: model })
    .then(response => response.json())
    .then(data => camelcaseKeys(data))
    .catch(httpError => handleHttpErrors(httpError, 'intervention', logger, { url, docNumber, data: JSON.stringify(model) }));
};

export const goToNextState = (docNumber: string, actualState: number, storeId: number, stateParam: StateParam = ''): Promise<InterventionState> => {
  const url = `${WORKSHOP_PROXY}/v2/stores/${storeId}/interventions/document_numbers/${docNumber}/states${stateParam}`;
  const model = { current_state: actualState };
  return buildWorkshopToolsRequest({ url, method: 'PATCH', body: model })
    .then(response => response.json())
    .then(data => camelcaseKeys(data, { deep: true }))
    .catch(httpError => handleHttpErrors(httpError, 'intervention', logger, { url, docNumber, data: JSON.stringify(model) }));
};

export const getShipmentPDF = (parcelNumber: string, storeId: number, isMobile?: boolean) => {
  const url = `${WORKSHOP_PROXY}/v2/stores/${storeId}/interventions/parcel_numbers/${parcelNumber}/pdf/shipment`;
  return buildWorkshopToolsRequest({ url, method: 'GET' })
    .then(response => response.blob())
    .then(blob => (isMobile ? saveAs(blob, `${parcelNumber}.pdf`) : printBlob(blob)))
    .catch(httpError => handleHttpErrors(httpError, 'intervention', logger, { url }));
};

export const getPendingSparePartInterventionsIds = (sparePartCode: number, storeId: number): Promise<string[]> => {
  const url = `${WORKSHOP_PROXY}/v2/stores/${storeId}/spare_parts/${sparePartCode}/interventions`;
  return buildWorkshopToolsRequest({ url, method: 'GET' })
    .then(response => response.json())
    .catch(httpError => handleHttpErrors(httpError, 'intervention', logger, { url }));
};

export const getInterventionSpareParts = (docNumber: string, storeId: number): Promise<SparePartWorkshop[]> => {
  const url = `${WORKSHOP_PROXY}/v2/stores/${storeId}/interventions/document_numbers/${docNumber}/spare_parts`;
  return buildWorkshopToolsRequest({ url, method: 'GET' })
    .then(response => response.json())
    .then(result => camelcaseKeys(result))
    .catch(httpError => handleHttpErrors(httpError, 'intervention', logger, { url, docNumber }));
};

const getNextParams = (headers: Headers) => {
  const nextHeader = headers.get('next');
  return nextHeader ? nextHeader.split('?')[1] : null;
};

export const deleteInterventionFiles = (docNumber: string, fileName: string, storeId: number) => {
  const url = `${WORKSHOP}/interventions/${storeId}/intervention/${docNumber}/file/${fileName}`;
  return buildWorkshopToolsRequest({ url, method: 'DELETE', body: undefined, emptyString: false, isFileRequest: true }).catch(httpError =>
    handleHttpErrors(httpError, 'intervention', logger, { url, docNumber, method: 'DELETE' })
  );
};

export const sendWorkshopIntervention = (docNumbers: string[], containerId: number, storeId: number): Promise<ShippingResponse> => {
  const body = {
    packaging_id: containerId,
    document_numbers: docNumbers,
  };

  const url = `${WORKSHOP_PROXY}/v2/stores/${storeId}/interventions/states/shipment`;
  return buildWorkshopToolsRequest({ url, method: 'POST', body })
    .then(response => response.json())
    .then((result: RawShippingReturn) => ({
      docNumber: result.interventions[0].document_number,
      state: {
        stateId: result.interventions[0].state.state_id,
        stateLabel: result.interventions[0].state.state_label,
      },
      parcelNumber: result.parcel_number,
    }))
    .catch(httpError => handleHttpErrors(httpError, 'intervention', logger, { url, docNumbers: docNumbers.join(','), data: JSON.stringify(body) }));
};

interface ConvertToQuotePayload {
  availability_date: string | null;
  customer_alert_type: string;
}

interface ConvertToQuoteProps {
  docNumber: string;
  availabilityDate: NullableDate;
  storeId: number;
}
export const convertQuoteToOrder = ({ docNumber, availabilityDate, storeId }: ConvertToQuoteProps): Promise<InterventionState> => {
  const { customer } = store.getState().customer;
  if (!customer) {
    throw new Error(t('intervention.page.customer.unfilled'));
  }
  const body: ConvertToQuotePayload = {
    availability_date: availabilityDate ? formatISOWithTimeZone(availabilityDate) : null,
    customer_alert_type: getInterventionCustomerAlert(customer),
  };
  const url = `${WORKSHOP_PROXY}/v2/stores/${storeId}/interventions/document_numbers/${docNumber}/quotes/validation`;
  return buildWorkshopToolsRequest({ url, method: 'PATCH', body })
    .then(response => response.json())
    .then(getCreationResponse)
    .then(result => result.state)
    .catch(httpError => handleHttpErrors(httpError, 'intervention', logger, { url, docNumber }));
};

const isInterventionReceived = (
  intervention: ParcelIntervention,
  interventions: ParcelIntervention[],
  isPending: boolean,
  invoiceNumber?: string,
  searchNumber?: string
) => {
  if (searchNumber) {
    return new Set([invoiceNumber, intervention.documentNumber]).has(searchNumber) || !isPending;
  }
  return intervention.received || interventions.every(inter => !inter.received);
};

const isInterventionPending = (interventionStateId: number, isRealizationSite: boolean) =>
  interventionStateId === InterventionStatus.forReceptionInRW ? isRealizationSite : !isRealizationSite;

const buildParcelInterventions = (
  storeId: number,
  parcel: Parcel,
  interventions: Intervention[],
  searchNumber?: string,
  forceNotReceived?: boolean
) =>
  parcel.interventions.map(parcelIntervention => {
    const intervention = interventions.find(i => i.interventionNumber === parcelIntervention.documentNumber);
    let isPending = false;
    let received = false;

    if (intervention) {
      isPending = !parcelIntervention.received && isInterventionPending(intervention.interventionStateId, storeId === intervention.realisationSiteId);
      received =
        !forceNotReceived && isInterventionReceived(parcelIntervention, parcel.interventions, isPending, intervention.invoiceNumber, searchNumber);
    }

    return {
      ...parcelIntervention,
      invoiceNumber: intervention?.invoiceNumber,
      received,
      product: intervention?.product,
      isPending,
      issued: !received && parcel.interventions.some(inter => inter.received),
    };
  });

const handleGetParcelError = (httpError: any, parcelNumber: string, url: string) => {
  if (httpError.response?.status === 403) {
    throw t('toaster.error.parcel.not.available', { parcel: parcelNumber });
  }
  if (httpError.response?.status === 404) {
    throw t('toaster.error.parcel.not.found', { parcel: parcelNumber });
  }
  return handleHttpErrors(httpError, 'intervention', logger, { url });
};

const transformParcel = (parcel: ParcelNumberRaw): Parcel => ({
  logisticalNumber: parcel.logistical_number,
  packagingId: parcel.packaging_id,
  parcelNumber: parcel.parcel_number,
  sender: parcel.sender,
  receiverSite: parcel.receiver_site,
  shipmentSite: parcel.shipment_site,
  shipmentDate: new Date(parcel.shipment_date),
  interventions: parcel.interventions.map(intervention => ({
    documentNumber: intervention.document_number,
    received: intervention.received,
    receptionDate: intervention.reception_date ? new Date(intervention.reception_date) : undefined,
  })),
});

export const getParcelInfo = (parcelNumber: string, storeId: number): Promise<Parcel> => {
  const headers = buildHeaders(true);
  const url = buildUrl('ICARE_BACK', `${WORKSHOP_PROXY}/v2/stores/${storeId}/parcel_numbers/${parcelNumber}`);
  const request = buildRequest(url, 'GET', headers);
  return fetchRequest(request)
    .then(response => response.json())
    .then((parcelRaw: ParcelNumberRaw) => transformParcel(parcelRaw))
    .catch(httpError => handleGetParcelError(httpError, parcelNumber, url));
};

export const getLogisticalInformation = (interventionId: string, storeId: number): Promise<Parcel[]> => {
  const isInvoice = interventionId.length === 16;
  const headers = buildHeaders(true);
  const url = buildUrl(
    'ICARE_BACK',
    `${WORKSHOP_PROXY}/v2/stores/${storeId}/interventions/${
      isInvoice ? 'invoice_numbers' : 'document_numbers'
    }/${interventionId}/logistical_information`
  );
  const request = buildRequest(url, 'GET', headers);

  return fetchRequest(request)
    .then(response => response.json())
    .then(data => camelcaseKeys<Parcel[]>(data, { deep: true }))
    .catch(httpError => {
      if (httpError.response?.status === 403) {
        throw t('toaster.error.intervention.not.available', { documentNumber: interventionId });
      }
      if (httpError.response?.status === 404) {
        throw t('toaster.error.intervention.not.found', { documentNumber: interventionId });
      }

      return handleHttpErrors(httpError, 'intervention', logger, { url });
    });
};

export const receiveParcel = (parcel: Parcel, storeId: number): Promise<Parcel> => {
  const body = {
    document_numbers: parcel.interventions
      .filter(intervention => intervention.received && intervention.isPending)
      .map(intervention => intervention.documentNumber),
  };
  const url = `${WORKSHOP_PROXY}/v2/stores/${storeId}/parcel_numbers/${parcel.parcelNumber}/reception`;

  return buildWorkshopToolsRequest({ url, method: 'PATCH', body })
    .then(response => response.json())
    .then(result => camelcaseKeys<Parcel>(result, { deep: true }))
    .then(result => ({
      ...result,
      parcelNumber: parcel.parcelNumber,
    }))
    .catch(httpError => handleHttpErrors(httpError, 'intervention', logger, { url, parcel: parcel.parcelNumber }));
};

export const getInterventionsByStates = (
  stateIds: number[],
  nextParams: string,
  storeId: number,
  isRealizationSite = false
): Promise<InterventionStatesPagination> => {
  const params = `&${nextParams}`;

  const states = `states=${stateIds.join(',')}`;
  const includes = '&includes=type.context_code';
  const param = `${states}&is_realisation_site=${isRealizationSite.toString()}${params}${includes}`;
  const url = `${WORKSHOP_PROXY}/v3/stores/${storeId}/interventions?${param}`;
  const emptyResult: InterventionStatesPagination = { total: 0, rawInterventions: [], futureParams: '' };
  return buildWorkshopV3Request(url, 'GET')
    .then(response => {
      if (response.status === 204) {
        return emptyResult;
      }
      return response.json().then(interventions => ({
        total: Number(response.headers.get('x-total-count')),
        rawInterventions: interventions.map(buildWorkshopInterventionFromRaw),
        futureParams: getNextParams(response.headers),
      }));
    })
    .catch(httpError => {
      if (httpError.response?.status === 404) {
        return emptyResult;
      }
      return handleHttpErrors(httpError, 'intervention', logger, { url });
    });
};

export const getInterventionsByIds = (interventionsIds: string[], include: string[], storeId: number): Promise<WorkshopIntervention[]> => {
  const chunkInterventionsIds = chunkArray(interventionsIds, 10);
  return Promise.all(
    chunkInterventionsIds.map(partialInterventionsIds => {
      const urlParams = `document_number_list=${partialInterventionsIds}&include=${include}`;
      const url = buildUrl('ICARE_BACK', `${WORKSHOP_PROXY}/v3/stores/${storeId}/interventions/document_numbers?${urlParams}`);
      const headers = buildHeaders(true);
      const request = buildRequest(url, 'GET', headers);
      return fetchRequest(request)
        .then(response => response.json())
        .then(interventions => interventions.map(buildWorkshopInterventionFromRaw))
        .catch(async httpError => {
          if (httpError instanceof HttpError) {
            if (httpError.response?.status === 404) {
              return [] as WorkshopIntervention[];
            }
            if (httpError.response?.status === 403) {
              throw await httpError.response.json();
            }
          }
          return handleHttpErrors(httpError, 'intervention', logger, { url });
        });
    })
  ).then(arrayOfInterventions => arrayOfInterventions.flat());
};

class ParcelError extends Error {
  constructor(
    public errorCode: number,
    public message: string
  ) {
    super(message);
    this.name = 'ParcelError';
  }
}

export const getFullParcel = async (storeId: number, parcelNumber: string, searchNumber?: string): Promise<Parcel> => {
  const headers = buildHeaders(true);
  const url = buildUrl('ICARE_BACK', `${WORKSHOP_PROXY}/v2/stores/${storeId}/parcel_numbers/${parcelNumber}`);
  const request = buildRequest(url, 'GET', headers);

  try {
    const result = await (await fetchRequest(request)).json();
    const parcel = camelcaseKeys<Parcel>(result, { deep: true });

    if (parcel.receiverSite !== storeId) {
      throw new ParcelError(999, t('parcels.flow.not.existing.parcel'));
    }

    const documentNumbers = parcel.interventions.map(parcelIntervention => parcelIntervention.documentNumber);
    const workshopInterventions = await getInterventionsByIds(documentNumbers, ['state'], storeId);
    const interventions = await fillInterventionsInformation({ interventions: workshopInterventions, withoutIdentity: true });

    return {
      ...parcel,
      parcelNumber,
      interventions: buildParcelInterventions(storeId, parcel, interventions, searchNumber),
    };
  } catch (httpError) {
    if (httpError instanceof ParcelError) {
      throw httpError;
    }
    return handleGetParcelError(httpError, parcelNumber, url);
  }
};

interface Historic {
  nextParams: string;
  parcels: Parcel[];
}

export const getHistoric = (
  startDate: Date,
  endDate: Date | undefined,
  isShipment: boolean,
  paginationParams: string,
  targetStoreId: number | null,
  storeId: number
): Promise<Historic> => {
  const start = getStartOfDay(startDate).toISOString();
  const end = getEndOfDay(endDate ?? startDate).toISOString();

  let params = `${paginationParams}&start_date=${start}&end_date=${end}`;
  if (targetStoreId) {
    params += `&${isShipment ? 'receiver' : 'sender'}=${targetStoreId}`;
  }

  const url = buildUrl('ICARE_BACK', `${WORKSHOP_PROXY}/v2/stores/${storeId}/parcels/historic/${isShipment ? 'shipments' : 'receptions'}?${params}`);

  const headers = buildHeaders(true);
  const request = buildRequest(url, 'GET', headers);
  return fetchRequest(request)
    .then(response => {
      if (response.status === 204) {
        return { nextParams: '', parcels: [] };
      }

      return response
        .json()
        .then((parcelsRaw: ParcelNumberRaw[]) => parcelsRaw.map(transformParcel))
        .then(parcels => {
          const nextParams = response.status === 206 ? getNextParams(response.headers) : '';
          return { parcels, nextParams };
        });
    })
    .catch(httpError => {
      if (httpError.response?.status === 404) {
        throw t('history.filters.board.no.results.found');
      }
      return handleHttpErrors(httpError, 'intervention', logger, { url });
    });
};

interface ParcelsAndParams {
  parcels: Parcel[];
  nextParams: string;
  error?: boolean;
}

const buildParcelWithInterventions = (parcelInfo: Parcel, parcelsInterventions: Intervention[], storeId: number) => {
  const parcelInterventions = parcelsInterventions.filter(parcelIntervention =>
    parcelInfo.interventions.map(intervention => intervention.documentNumber).includes(parcelIntervention.interventionNumber)
  );
  const interventionsWithProduct = buildParcelInterventions(storeId, parcelInfo, parcelInterventions, undefined, true);

  return {
    ...parcelInfo,
    parcelNumber: parcelInfo.parcelNumber,
    interventions: interventionsWithProduct,
  };
};

export const getParcelsToReceive = async (storeId: number, params: string | null): Promise<ParcelsAndParams> => {
  const url = `${WORKSHOP_PROXY}/v2/stores/${storeId}/parcels/receptions?${params ?? ''}`;

  try {
    const response = await buildWorkshopToolsRequest({ url, method: 'GET' });

    if (response.status === 204) {
      return { parcels: [], nextParams: '' };
    }

    const data: ParcelNumberRaw[] = await response.json();
    const parcels = data.map(transformParcel);
    const nextParams = response.status === 206 ? (getNextParams(response.headers) ?? '') : '';
    const documentNumbers = parcels.flatMap(parcel => parcel.interventions.map(intervention => intervention.documentNumber));

    try {
      const wsInterventions = await getInterventionsByIds(documentNumbers, ['state'], storeId);
      const parcelsInterventions = await fillInterventionsInformation({ interventions: wsInterventions, withoutIdentity: true });

      return {
        parcels: parcels.map(parcelInfo => buildParcelWithInterventions(parcelInfo, parcelsInterventions, storeId)),
        nextParams,
      };
    } catch {
      return { parcels: [], nextParams, error: true };
    }
  } catch (httpError) {
    if (httpError instanceof HttpError) {
      return handle404Errors(httpError, logger, url, { nextParams: '', parcels: [] });
    }
    throw httpError;
  }
};

interface WorkLoad {
  familyId: number;
  interventionsToReceiveCount: number;
  interventionsToReceiveTime: number;
  interventionsToTreatCount: number;
  interventionsToTreatTime: number;
}

export interface LoadForecast {
  familyId: number;
  toReceiveCount: number;
  toReceiveTime: number;
  toTreatCount: number;
  toTreatTime: number;
  totalCount: number;
  totalTime: number;
}

export const getLoadForecast = (storeId: number): Promise<LoadForecast[]> => {
  const url = `${WORKSHOP_PROXY}/v2/stores/${storeId}/workload`;
  return buildWorkshopToolsRequest({ url, method: 'GET' })
    .then(response => response.json())
    .then(data => camelcaseKeys<WorkLoad[]>(data, { deep: true }))
    .then(forecasts =>
      forecasts.map(forecast => ({
        familyId: forecast.familyId,
        toReceiveCount: forecast.interventionsToReceiveCount,
        toTreatCount: forecast.interventionsToTreatCount,
        toReceiveTime: forecast.interventionsToReceiveTime,
        toTreatTime: forecast.interventionsToTreatTime,
        totalCount: forecast.interventionsToTreatCount + forecast.interventionsToReceiveCount,
        totalTime: forecast.interventionsToTreatTime + forecast.interventionsToReceiveTime,
      }))
    )
    .catch(httpError => handleHttpErrors(httpError, 'intervention', logger, { url }));
};

export const getPurchaseInterventions = (memberId: string, articleId: number): Promise<PurchaseIntervention[]> => {
  const headers = buildHeaders(true);
  const url = buildUrl('ICARE_BACK', `workshop/customer_purchase_interventions/${memberId}/${articleId}`);
  const request = buildRequest(url, 'GET', headers);
  return fetchRequest(request)
    .then(response => response.json())
    .catch(httpError => handleHttpErrors(httpError, 'intervention', logger, { url }));
};

export const updateLocation = (docNumber: string, location: string, storeId: number) => {
  const url = `${WORKSHOP_PROXY}/v2/stores/${storeId}/interventions/document_numbers/${docNumber}/location`;
  const body = { location };

  return buildWorkshopToolsRequest({ url, method: 'PATCH', body, emptyString: true }).catch(httpError =>
    handleHttpErrors(httpError, 'intervention', logger, { url })
  );
};

export const getInterventionsFromExternalRef = (externalRef: string): Promise<SearchIntervention[]> => {
  const url = `${WORKSHOP_PROXY}/v3/external_reference_number/${externalRef}/interventions`;
  return buildWorkshopV3Request(url, 'GET')
    .then(response => response.json())
    .then(result => camelcaseKeys<WorkshopIntervention[]>(result, { deep: true }))
    .then(interventions =>
      Promise.all(
        interventions.map(intervention =>
          fetchFullCustomerById(intervention.memberId)
            .catch(() => null)
            .then(result => ({
              interventionNumber: intervention.documentNumber,
              customer: result ? getCustomerName(result) : '',
              product: intervention.depositedProduct.label,
              creationDate: new Date(intervention.creationDate),
              customerPledge: intervention.availabilityDate ? new Date(intervention.availabilityDate) : getCurrentDate(),
              estimatedTime: intervention.totalTheoreticalTime,
            }))
        )
      )
    )
    .catch(httpError => handleHttpErrors(httpError, 'intervention', logger, { url }));
};
